import React, { useCallback, useEffect, useState } from 'react';

import type { LocalConsoleContext } from '../../types';

import ActivePanel from './ActivePanel';
import DormantPanel from './DormantPanel';
import EventLog from './EventLog';
import NotAuthorized from './NotAuthorized';
import OfflineModeBroadcast from './OfflineModeBroadcast';
import OfflineModeConfirmation from './OfflineModeConfirmation';
import ReachSummary from './ReachSummary';
import styles from './OfflineMode.module.scss';
import { DIVERGENCE_UNRESOLVED, PANEL_TITLE, WRITE_ERROR, pageDescription } from './copy';
import type { OfflineModeDirection } from 'redux-stores/offlineMode';
import { actions, thunk, useAppDispatch, useAppSelector } from 'redux-stores';

export default function OfflineModeSection({
  organizationId,
  organizationName,
  access,
}: LocalConsoleContext) {
  const dispatch = useAppDispatch();
  const { activation, status, isSwitching, writeError, writeErrorDirection, lastActivation } =
    useAppSelector((state) => state.offlineMode);

  const [confirming, setConfirming] = useState<OfflineModeDirection | null>(null);
  const [composingFor, setComposingFor] = useState<boolean | null>(null);

  const canAdminister = access.canAdministerOfflineMode;

  const load = useCallback(() => {
    thunk.fetchOfflineModeActivation(dispatch, organizationId);
    thunk.fetchOfflineModeLastActivation(dispatch, organizationId);
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!canAdminister || !organizationId) return;
    load();
  }, [canAdminister, load, organizationId]);

  const onConfirm = useCallback(async () => {
    if (!confirming) return;
    await thunk.switchOfflineMode(dispatch, {
      organizationId,
      direction: confirming,
      isGrandfathered: access.isGrandfathered,
      onSuccess: () => {
        setConfirming(null);
        thunk.fetchOfflineModeLastActivation(dispatch, organizationId);
      },
    });
  }, [access.isGrandfathered, confirming, dispatch, organizationId]);

  const onRetryWrite = useCallback(() => {
    if (writeErrorDirection) setConfirming(writeErrorDirection);
    dispatch(actions.clearOfflineModeWriteError());
  }, [dispatch, writeErrorDirection]);

  const isActive = activation?.state === 'on';

  const closeCompose = () => setComposingFor(null);

  const broadcast =
    composingFor === isActive ? (
      <OfflineModeBroadcast
        onCancel={closeCompose}
        onSent={closeCompose}
        organizationId={organizationId}
        organizationName={organizationName}
      />
    ) : undefined;

  if (!access.isResolved) return null;

  if (!canAdminister) {
    return <NotAuthorized isGrandfathered={access.isGrandfathered} />;
  }

  if (status === 'loading' && !activation) {
    return (
      <div data-test-id="offlineModeLoading" className={styles.page}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{PANEL_TITLE}</h1>
        </div>
        <p className={styles.body}>Reading the activation state from the local server…</p>
      </div>
    );
  }

  if (status === 'error' && !activation) {
    return (
      <div data-test-id="offlineModeLoadError" className={styles.page}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{PANEL_TITLE}</h1>
        </div>
        <p className={styles.body}>
          The local server did not return a usable activation state, so this panel cannot say
          whether Offline Mode is on. Nothing has been changed.
        </p>
        <button
          type="button"
          className={styles.secondaryAction}
          data-test-id="offlineModeLoadRetry"
          onClick={load}
        >
          {WRITE_ERROR.retryLabel}
        </button>
      </div>
    );
  }

  if (!activation) return null;

  return (
    <div className={styles.page} data-test-id="offlineModeSection">
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{PANEL_TITLE}</h1>
        {isActive ? (
          <span className={styles.activeTag} data-test-id="offlineModeActiveTag">
            Active
          </span>
        ) : null}
      </div>

      <p className={styles.pageDescription} data-test-id="offlineModePageDescription">
        {pageDescription(organizationName)}
      </p>

      {activation.divergence ? (
        <div className={styles.writeError} role="alert" data-test-id="offlineModeDivergence">
          <span className={styles.writeErrorBody}>{DIVERGENCE_UNRESOLVED}</span>
        </div>
      ) : null}

      {writeError ? (
        <div className={styles.writeError} role="alert" data-test-id="offlineModeWriteError">
          <span className={styles.writeErrorHeading}>{WRITE_ERROR.heading}</span>
          <span className={styles.writeErrorBody}>{writeError}</span>
          <div className={styles.writeErrorActions}>
            <button
              type="button"
              className={styles.secondaryAction}
              data-test-id="offlineModeWriteRetry"
              onClick={onRetryWrite}
            >
              {WRITE_ERROR.retryLabel}
            </button>
            <button
              type="button"
              className={styles.tertiaryAction}
              data-test-id="offlineModeWriteDismiss"
              onClick={() => dispatch(actions.clearOfflineModeWriteError())}
            >
              {WRITE_ERROR.dismissLabel}
            </button>
          </div>
        </div>
      ) : null}

      {isActive ? (
        <ActivePanel
          activation={activation}
          isSwitching={isSwitching}
          broadcast={broadcast}
          onReturn={() => setConfirming('return')}
          onSendBroadcast={() => setComposingFor(true)}
          organizationName={organizationName}
          eventLog={
            <EventLog activeSince={activation.updatedAt} isActive organizationId={organizationId} />
          }
        />
      ) : (
        <DormantPanel
          broadcast={broadcast}
          eventLog={
            lastActivation ? (
              <EventLog
                activeSince={lastActivation.startedAt}
                isActive={false}
                organizationId={organizationId}
              />
            ) : undefined
          }
          isSwitching={isSwitching}
          lastActivation={lastActivation}
          onActivate={() => setConfirming('activate')}
          onSendReturnBroadcast={() => setComposingFor(false)}
          organizationName={organizationName}
        />
      )}

      {confirming ? (
        <OfflineModeConfirmation
          direction={confirming}
          isOpen
          isSwitching={isSwitching}
          onCancel={() => setConfirming(null)}
          onConfirm={onConfirm}
          organizationName={organizationName}
          reachSummary={<ReachSummary direction={confirming} organizationId={organizationId} />}
        />
      ) : null}
    </div>
  );
}
